import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Trash2, Plus, Minus, ArrowRight, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { api, money } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Input } from "@/components/ui/input";

export default function Cart() {
  const { items, removeItem, updateQty, subtotal } = useCart();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const checkout = async () => {
    if (items.length === 0) return;
    setLoading(true);
    try {
      const payload = {
        origin_url: window.location.origin,
        email: email || null,
        items: items.map((i) => ({
          product_id: i.product_id,
          quantity: i.quantity,
          customization: {
            text_lines: i.customization?.text_lines || [],
            has_logo: !!i.customization?.has_logo,
            font: i.customization?.font,
            placement: i.customization?.placement,
            finish: i.customization?.finish,
            notes: i.customization?.notes,
          },
        })),
      };
      const { data } = await api.post("/payments/checkout", payload);
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error("Could not start checkout. Please try again.");
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return (
      <div data-testid="cart-empty" className="pt-40 pb-40 text-center">
        <ShoppingBag className="w-12 h-12 mx-auto text-primary mb-6" />
        <h1 className="font-display text-5xl mb-4">Your cart is empty</h1>
        <p className="font-body text-muted-foreground mb-8">Every piece starts with a single mark.</p>
        <Link to="/shop" className="btn-forge inline-flex items-center gap-2 bg-primary text-primary-foreground px-8 py-4 font-num uppercase tracking-widest text-sm hover:bg-white">
          Browse the collection <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="cart-page" className="pt-32 pb-32">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <h1 className="font-display text-6xl md:text-7xl mb-12">Your Cart</h1>
        <div className="grid lg:grid-cols-[1.7fr_1fr] gap-12">
          <div className="border-t border-white/10">
            <AnimatePresence>
              {items.map((i) => (
                <motion.div
                  key={i.lineId}
                  layout
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0, x: -30 }}
                  data-testid={`cart-item-${i.product_id}`}
                  className="flex gap-5 py-6 border-b border-white/10"
                >
                  <div className="relative w-24 h-28 shrink-0 overflow-hidden border border-white/10 bg-[#111]">
                    <img src={i.image} alt={i.name} className="w-full h-full object-cover opacity-70" />
                    {i.customization?.logo && (
                      <img src={i.customization.logo} alt="logo" className="absolute inset-0 m-auto max-h-8 max-w-[70%] object-contain" style={{ filter: "brightness(1.6) grayscale(1)" }} />
                    )}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-display text-2xl">{i.name}</h3>
                    <p className="font-body text-xs text-muted-foreground mt-1">
                      {(i.customization?.text_lines || []).filter(Boolean).join(" · ") || "Logo only"}
                    </p>
                    <p className="font-body text-xs text-muted-foreground capitalize">
                      {i.customization?.finish} · {i.customization?.font} · {i.customization?.placement}
                      {i.customization?.has_logo ? " · +logo" : ""}
                    </p>
                    <div className="flex items-center gap-4 mt-4">
                      <div className="flex items-center border border-white/15">
                        <button onClick={() => updateQty(i.lineId, i.quantity - 1)} className="p-2 hover:text-primary" aria-label="decrease"><Minus className="w-3.5 h-3.5" /></button>
                        <span className="w-8 text-center font-num text-sm">{i.quantity}</span>
                        <button onClick={() => updateQty(i.lineId, i.quantity + 1)} className="p-2 hover:text-primary" aria-label="increase"><Plus className="w-3.5 h-3.5" /></button>
                      </div>
                      <button data-testid={`remove-item-${i.product_id}`} onClick={() => removeItem(i.lineId)} className="text-muted-foreground hover:text-primary" aria-label="remove"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                  <div className="font-num text-lg">{money(i.unit_price * i.quantity)}</div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>

          <div className="lg:sticky lg:top-28 h-fit border border-white/10 bg-[#0d0d0d] p-8">
            <h2 className="font-num uppercase tracking-widest text-sm text-muted-foreground mb-6">Order Summary</h2>
            <div className="flex justify-between font-body mb-3"><span className="text-muted-foreground">Subtotal</span><span className="font-num">{money(subtotal)}</span></div>
            <div className="flex justify-between font-body mb-6"><span className="text-muted-foreground">Tax & shipping</span><span className="font-body text-sm text-muted-foreground">Calculated at checkout</span></div>
            <div className="border-t border-white/10 pt-4 flex justify-between items-baseline mb-8">
              <span className="font-display text-2xl">Total</span>
              <span className="font-num text-2xl text-primary">{money(subtotal)}</span>
            </div>
            <label className="font-num uppercase tracking-widest text-xs text-muted-foreground">Email for order updates</label>
            <Input data-testid="checkout-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" className="bg-[#111] border-white/15 rounded-none h-12 mt-3 mb-6 focus-visible:ring-primary" />
            <button
              data-testid="checkout-btn"
              onClick={checkout}
              disabled={loading}
              className="btn-forge group w-full flex items-center justify-center gap-3 bg-primary text-primary-foreground px-8 py-4 font-num uppercase tracking-widest text-sm hover:bg-white disabled:opacity-60"
            >
              {loading ? "Redirecting…" : <>Secure checkout <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></>}
            </button>
            <p className="font-body text-xs text-muted-foreground text-center mt-4">Powered by Stripe · Test card 4242 4242 4242 4242</p>
          </div>
        </div>
      </div>
    </div>
  );
}
